/**
 * Wave B — view, display, workplane and UI commands.
 * (view.* / vis.* / sys.* UI entries; docs/architecture/command-parity-plan.md)
 */

import { runInteraction, PT, PN, valueAt, solidEntity } from './lib';
import { undoableAdd } from '../entityOps';
import { useAppStore } from '../../../state/store';
import { useViewStore } from '../../../state/commandAuxStores';
import { useModelViewportsStore, type ModelVportsConfig } from '../../../state/useModelViewportsStore';
// Direct import: the snapping barrel also exports the drei-based SnapIndicator.
import { useSnapStore } from '../../../tools/snapping/SnapManager';
import type { RunnerMap } from '../runnerRegistry';
import { notify } from '../../../utils/notify';
import { cycleIsoPlane } from '../../drafting/isoPlane';
import { captureViewportPng } from '../../../services/mcp/viewportCapture';
import i18n from '../../../i18n';

function setView(name: string): void {
    window.dispatchEvent(new CustomEvent('yap:set-view', { detail: { view: name } }));
}

/** MENUBAR/RIBBON/LAYOUTTAB argument resolution: "0"/"OFF" -> false,
 *  "1"/"ON" -> true, anything else -> the provided toggle value. */
function parseToggle(args: string[] | undefined, fallback: boolean): boolean {
    const raw = args?.[0]?.trim().toUpperCase();
    if (!raw) return fallback;
    if (['1', 'ON', 'TRUE', 'YES'].includes(raw)) return true;
    if (['0', 'OFF', 'FALSE', 'NO'].includes(raw)) return false;
    return fallback;
}

export const viewRunners: RunnerMap = {
    // UCSICON (Faz 11.10): ON/ORIGIN/OFF drive useViewStore.ucsIcon; a bare
    // command toggles between origin and off (the classic no-arg toggle).
    'axes.ucsicon': (args) => {
        const store = useViewStore.getState();
        const raw = args?.[0]?.trim().toUpperCase();
        if (!raw || raw === 'TOGGLE') {
            const next = store.ucsIcon === 'off' ? 'origin' : 'off';
            store.set('ucsIcon', next);
            return `UCSICON ${next.toUpperCase()}`;
        }
        if (raw === 'ON' || raw === 'ORIGIN' || raw === '1') {
            store.set('ucsIcon', raw === 'ON' ? 'on' : 'origin');
            return `UCSICON ${raw}`;
        }
        if (raw === 'OFF' || raw === '0') {
            store.set('ucsIcon', 'off');
            return 'UCSICON OFF';
        }
        if (raw === '?') {
            return `UCSICON = ${store.ucsIcon.toUpperCase()}`;
        }
        return 'UCSICON: usage — UCSICON [ON/ORIGIN/OFF]';
    },
    'view.standard': (args) => {
        const name = (args?.[0] ?? 'Top').toLowerCase();
        const map: Record<string, string> = { top: 'top', bottom: 'bottom', front: 'front', back: 'back', right: 'right', left: 'left', persp: 'perspective', perspective: 'perspective' };
        if (map[name]) { setView(map[name]); return `View set: ${name}.`; }
        runInteraction({
            steps: [{ prompt: 'VIEW: enter view name (Top/Front/Right/Perspective)', kind: 'text' }],
            complete: (_p, values) => {
                const v = String(values[0] ?? 'top').toLowerCase();
                if (map[v]) { setView(map[v]); return `View set: ${v}.`; }
                return `VIEW: unknown name "${v}".`;
            },
        });
        return 'VIEW: specify name';
    },
    'view.namedViews': (args) => {
        const sub = (args?.[0] ?? 'list').toLowerCase();
        const store = useViewStore.getState();
        if (sub === 'save' || args?.[0]?.toLowerCase() === 's') {
            runInteraction({
                steps: [{ prompt: 'NAMEVIEW: save current view as', kind: 'text' }],
                complete: (_p, values) => {
                    const v = useAppStore.getState().view;
                    store.saveView({
                        name: String(values[0] ?? `View-${Date.now()}`),
                        position: [...v.camera.position] as [number, number, number],
                        target: [...v.camera.target] as [number, number, number],
                    });
                    return `Named view "${values[0]}" saved.`;
                },
            });
            return 'NAMEVIEWS: enter name';
        }
        if (sub === 'list' || args?.length === 0) {
            const list = useViewStore.getState().namedViews;
            return list.length ? `Named views: ${list.map((n) => n.name).join(', ')}` : 'No named views yet (VIEW > Save).';
        }
        const restore = store.restoreView(sub);
        if (restore) {
            useAppStore.getState().updateView({
                camera: { ...useAppStore.getState().view.camera, position: restore.position, target: restore.target },
            });
            setView('perspective');
            return `Restored view "${sub}".`;
        }
        return `VIEW: no saved view "${sub}".`;
    },
    'view.plan': () => { setView('top'); return 'Plan view (current workplane).'; },
    'view.cplaneAuto': () => {
        useAppStore.getState().setActiveTool('SET_WORK_PLANE');
        return 'AUTOCPANE: workplane picking armed — click a face/object.';
    },
    'view.polar': () => {
        const s = useViewStore.getState();
        s.set('polarOn', !s.polarOn);
        notify(i18n.t('tools.notify.polarToggled', { state: !s.polarOn ? i18n.t('tools.notify.stateOnUpper') : i18n.t('tools.notify.stateOffUpper') }), 'info');
        return `Polar tracking ${!s.polarOn ? 'ON' : 'OFF'}.`;
    },
    // ISODRAFT (Faz 7.1): the master switch is session-scoped (OCS made it
    // non-persistent on purpose — a persistent ON bent every crosshair);
    // `ISODRAFT Left/Top/Right` also picks the plane, bare toggles.
    'view.isodraft': (args) => {
        const raw = (args?.[0] ?? '').toLowerCase();
        const v = useViewStore.getState();
        if (raw === 'left' || raw === 'top' || raw === 'right') {
            v.set('isoplane', raw);
            if (!v.isoDrafting) v.set('isoDrafting', true);
            return `ISODRAFT on — plane ${raw.toUpperCase()}.`;
        }
        if (raw === 'off' || raw === '0') {
            v.set('isoDrafting', false);
            return 'ISODRAFT off.';
        }
        if (raw === 'on' || raw === '1') {
            v.set('isoDrafting', true);
            const plane = v.isoplane === 'off' ? 'left' : v.isoplane;
            return `ISODRAFT on — plane ${plane.toUpperCase()}.`;
        }
        const next = !v.isoDrafting;
        v.set('isoDrafting', next);
        return next
            ? `ISODRAFT on — plane ${(v.isoplane === 'off' ? 'left' : v.isoplane).toUpperCase()}.`
            : 'ISODRAFT off.';
    },
    // ISOPLANE (Faz 7.1): pick the drawing plane (persistent), turning the
    // isodraft master on; a bare ISOPLANE cycles Left → Top → Right (F5).
    'view.isoplane': (args) => {
        const raw = (args?.[0] ?? '').toLowerCase();
        const v = useViewStore.getState();
        if (raw === 'left' || raw === 'top' || raw === 'right') {
            v.set('isoplane', raw);
            if (!v.isoDrafting) v.set('isoDrafting', true);
            return `ISOPLANE ${raw.toUpperCase()}.`;
        }
        const next = cycleIsoPlane();
        return `ISOPLANE ${next.toUpperCase()}.`;
    },
    'view.smartTrack': () => {
        const s = useViewStore.getState();
        s.set('smartTrack', !s.smartTrack);
        return `SmartTrack ${!s.smartTrack ? 'ON' : 'OFF'}.`;
    },
    'view.workspace': (args) => {
        const mode = (args?.[0] ?? 'drafting').toLowerCase();
        const map: Record<string, string> = { drafting: '2d', '2d': '2d', annotation: '2d', '3d': '3d', modeling: '3d', bim: 'bim', architecture: 'bim', sketch: 'sketch', freeform: 'sketch' };
        const target = map[mode];
        if (!target) return `Workspace "${mode}" unknown (drafting/annotation, 3D modeling, architecture).`;
        window.dispatchEvent(new CustomEvent('yap:set-workspace', { detail: { workspace: target } }));
        return `Workspace switched to "${mode}".`;
    },
    'view.toolPalette': () => {
        notify(i18n.t('tools.notify.toolPalettesEnableTheSidebarLayers'), 'info');
        return 'Tool Palettes: sidebar opened as palette dock.';
    },
    'view.turntable': () => {
        const s = useViewStore.getState();
        s.set('turntable', !s.turntable);
        window.dispatchEvent(new CustomEvent('yap:turntable', { detail: { on: !s.turntable } }));
        return `Turntable ${!s.turntable ? 'ON (click/Esc to stop)' : 'OFF'}.`;
    },
    'view.camera': () => {
        useAppStore.getState().setActiveTool('VIEW_CAMERA');
        return 'CAMERA: placement armed — click eye then target.';
    },
    'vis.render': () => {
        // Render-to-PNG of the current viewport. Routed through the MCP
        // capture bridge (it forces a synchronous render and reads the buffer
        // inside the same frame task); a bare canvas.toDataURL would grab a
        // blank frame now that preserveDrawingBuffer is off.
        const canvas = document.querySelector('canvas');
        if (!canvas) return 'RENDER: no viewport canvas.';
        notify(i18n.t('tools.notify.renderRequestedExportingViewportImage'), 'info');
        captureViewportPng().then((base64) => {
            const a = document.createElement('a');
            a.href = `data:image/png;base64,${base64}`;
            a.download = `yap-render-${Date.now()}.png`;
            a.click();
            notify(i18n.t('tools.notify.renderSaved'), 'success');
        }).catch(() => {
            notify(i18n.t('tools.notify.renderViewportCaptureFailed'), 'warning');
        });
        return 'RENDER: viewport captured to PNG download.';
    },
    'vis.light': () => {
        runInteraction({
            steps: [PT('LIGHT: position'), PT('LIGHT: target (direction)')],
            complete: (pts) => {
                // Real visible marker: a small luminous diamond octahedron
                // (0-index meshes rendered nothing — that was a flag-only
                // lie). Shadows remain a view-mode toggle.
                const p = [pts[0].x, pts[0].y, pts[0].z] as [number, number, number];
                const s = 0.15;
                const vertices = [
                    [p[0], p[1], p[2] + s], [p[0], p[1], p[2] - s],
                    [p[0] + s, p[1], p[2]], [p[0] - s, p[1], p[2]],
                    [p[0], p[1] + s, p[2]], [p[0], p[1] - s, p[2]],
                ];
                const indices = [
                    [0, 2, 4], [0, 4, 3], [0, 3, 5], [0, 5, 2],
                    [1, 4, 2], [1, 3, 4], [1, 5, 3], [1, 2, 5],
                ];
                undoableAdd([solidEntity({ vertices, indices }, `Light-${Date.now()}`, { color: '#ffee66' })]);
                useAppStore.getState().updateView({ shadowsEnabled: true });
                return 'Point light placed (shadow rendering enabled).';
            },
        });
        return 'LIGHT: specify position';
    },
    'vis.styles': () => {
        const order = ['wireframe', 'shaded', 'rendered', 'xray', 'ghosted', 'hiddenLine'] as const;
        const app = useAppStore.getState();
        const idx = order.indexOf(app.view.viewMode as typeof order[number]);
        app.updateView({ viewMode: order[(idx + 1) % order.length] });
        return `Display mode: ${order[(idx + 1) % order.length]}.`;
    },
    'view.pan': () => {
        runInteraction({
            steps: [PT('PAN: base point'), PT('PAN: target point (or Enter for middle-mouse drag)')],
            complete: (pts) => {
                const dx = pts[1].x - pts[0].x, dy = pts[1].y - pts[0].y;
                window.dispatchEvent(new CustomEvent('yap:pan-by', { detail: { dx, dy } }));
                return `Panned by ${dx.toFixed(2)}, ${dy.toFixed(2)}.`;
            },
        });
        return 'PAN: specify base point (middle-mouse drag also pans).';
    },
    'sys.menuBar': (args) => {
        // MENUBAR 0/1 (Faz 3.6): toggles — or with an argument forces — the
        // classic menu bar row. RIBBON keeps toggling its own flag so the
        // two interface switches stay independent.
        const s = useViewStore.getState();
        const next = parseToggle(args, !s.menubarVisible);
        s.set('menubarVisible', next);
        return `Menu bar ${next ? 'ON' : 'OFF'}.`;
    },
    'sys.ribbonToggle': (args) => {
        // RIBBON / RIBBONCLOSE 0/1: ribbon (vs classic toolbar) shell.
        const s = useViewStore.getState();
        const next = parseToggle(args, !s.ribbonVisible);
        s.set('ribbonVisible', next);
        window.dispatchEvent(new CustomEvent('yap:ui-toggle', { detail: { ribbon: next } }));
        return `Ribbon ${next ? 'ON' : 'OFF'}.`;
    },
    'view.layoutTabs': (args) => {
        // LAYOUTTAB 0/1 (Faz 3.6): the Model | layout tab strip.
        const s = useViewStore.getState();
        const next = parseToggle(args, !s.layoutTabsVisible);
        s.set('layoutTabsVisible', next);
        return `Layout tabs ${next ? 'ON' : 'OFF'}.`;
    },
    // ---------------- Faz 4 status-bar / tiling commands ----------------
    'view.lwDisplay': (args) => {
        // LWDISPLAY 0/1: stored lineweights vs hairline rendering.
        const s = useViewStore.getState();
        const next = parseToggle(args, !s.showLineweights);
        s.set('showLineweights', next);
        return `Lineweight display ${next ? 'ON' : 'OFF (hairline lines)'}.`;
    },
    'view.transparencyDisplay': (args) => {
        // TRANSPARENCY 0/1: entity transparency vs force-opaque.
        const s = useViewStore.getState();
        const next = parseToggle(args, !s.showTransparency);
        s.set('showTransparency', next);
        return `Transparency display ${next ? 'ON' : 'OFF (force opaque)'}.`;
    },
    'view.otrack': (args) => {
        // OTRACK 0/1 (F11): object snap tracking alignment guides.
        const snap = useSnapStore.getState();
        const next = parseToggle(args, !snap.objectSnapTracking);
        if (snap.objectSnapTracking !== next) snap.toggleObjectSnapTracking();
        return `Object snap tracking ${next ? 'ON' : 'OFF'}.`;
    },
    'view.cleanScreen': (args) => {
        // CLEANSCREEN 0/1: hide ribbon/menubar/statusbar chrome + dock strips.
        const s = useViewStore.getState();
        const next = parseToggle(args, !s.cleanScreen);
        s.set('cleanScreen', next);
        return `Clean screen ${next ? 'ON — press again or CLEANSCREEN to restore' : 'OFF'}.`;
    },
    'view.vports': (args) => {
        // VPORTS (Faz 4.9 + 10.5): model-space viewport tiling. OCS accepts
        // the SIngle/2H/2V/4 pane configurations; bare VPORTS opens the
        // named-configurations dialog (AutoCAD parity — Save/Restore/Delete/
        // Join live there, Faz 10.5).
        const raw = (args?.[0] ?? '').trim().toUpperCase();
        const map: Record<string, ModelVportsConfig> = {
            'SI': 'single', 'SINGLE': 'single', '1': 'single',
            '2H': '2h', 'H': '2h',
            '2V': '2v', 'V': '2v',
            '4': '4',
        };
        const store = useModelViewportsStore.getState();
        if (!raw) {
            useAppStore.getState().openManageDialog('vports');
            return 'VPORTS: opening the viewport configurations dialog.';
        }
        const config = map[raw];
        if (!config) return 'VPORTS: usage — VPORTS (dialog) | Single | 2H | 2V | 4.';
        store.setConfig(config);
        store.setActiveCell(0);
        return `VPORTS: model space tiled to ${config.toUpperCase()} — click a cell to make it the command target.`;
    },
    // VPJOIN (Faz 10.5 — AutoCAD Join; OCS has no Join verb): merge two
    // adjacent cells of the current tiling into one viewport.
    'view.vportsJoin': (args) => {
        const store = useModelViewportsStore.getState();
        const a = Number(args?.[0]), b = Number(args?.[1]);
        if (Number.isInteger(a) && Number.isInteger(b) && a > 0 && b > 0) {
            const err = store.joinCells(a - 1, b - 1);
            return err ?? `VPJOIN: viewports ${a} and ${b} joined.`;
        }
        runInteraction({
            steps: [
                PN('VPJOIN: dominant viewport number <1>', 1),
                PN('VPJOIN: viewport to join <2>', 2),
            ],
            complete: (_p, values) => {
                const ia = Math.round(valueAt(values, 0, 1));
                const ib = Math.round(valueAt(values, 1, 2));
                const err = useModelViewportsStore.getState().joinCells(ia - 1, ib - 1);
                return err ?? `VPJOIN: viewports ${ia} and ${ib} joined.`;
            },
        });
        return 'VPJOIN: pick the dominant viewport';
    },
    'view.vportsSingle': () => {
        // Ribbon Single face (no name pool — not typeable).
        useModelViewportsStore.getState().setConfig('single');
        useModelViewportsStore.getState().setActiveCell(0);
        return 'VPORTS: restored the single model viewport.';
    },
    'view.cascade': () => {
        // OCS truth: CASCADE has no MDI windows — it delegates to a VPORTS
        // configuration (the 4-pane grid here).
        useModelViewportsStore.getState().setConfig('4');
        useModelViewportsStore.getState().setActiveCell(0);
        return 'CASCADE: delegated to VPORTS 4 (four model viewports).';
    },
    'view.horizontal': () => {
        useModelViewportsStore.getState().setConfig('2h');
        useModelViewportsStore.getState().setActiveCell(0);
        return 'HORIZONTAL: delegated to VPORTS 2H (two stacked viewports).';
    },
    'view.vertical': () => {
        useModelViewportsStore.getState().setConfig('2v');
        useModelViewportsStore.getState().setActiveCell(0);
        return 'VERTICAL: delegated to VPORTS 2V (two side-by-side viewports).';
    },
    'sys.history': () => 'Command history shown in the command line panel (scroll up).',
    'sys.options': () => {
        useAppStore.getState().openManageDialog('commandPresets');
        return 'OPTIONS: opening settings hub (Command Presets).';
    },
    'sys.settings': () => 'Settings: see File > Settings for units/appearance.',
};


